import { useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { CategoriaDeMeta, DiferenciaDeCoste, ExtractoDeYCloud, ResolucionDeDiferencia } from '../../api/ops/backoffice';
import { CampoImporte } from '../../componentes/CampoImporte';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { errorDeCampo } from '../../logica/acceso';
import { aCentimos, dinero, errorDeImporte } from '../../logica/dinero';
import { claveMes, type Mes } from '../../partners/logica';
import { ERROR_MOTIVO, motivoValido, textoDeError } from '../negocios/logica';
import {
    CATEGORIAS,
    CATEGORIAS_COSTE,
    ERROR_COSTE,
    ERROR_MENSAJES,
    ERROR_REFERENCIA_RECARGA,
    MONEDA_MONEDERO,
    NOTA_COSTE,
    PAISES,
    RESOLUCIONES,
    aDiezmilesimas,
    iniciosDeTrimestre,
    leerCsvYCloud,
    mensajesDeAjuste,
    mesFuturo,
    textoDelMes,
    textoResolver,
    type CsvLeido,
    type FilaConciliacion,
} from './logica';

/**
 * **Resolver una diferencia** (prototipo `vy`, `resolveWhatsAppCostDiscrepancy`): corregir el registro, ajustar el paquete
 * del negocio (con los mensajes que se ajustan, con signo), reclamar a YCloud o asumir el coste. Siempre con motivo.
 */
export function VentanaResolver({ ops, movil, fila, discrepancia, resolucion, onCerrar, onHecho }: { ops: Operaciones; movil: boolean; fila: FilaConciliacion; discrepancia: DiferenciaDeCoste; resolucion: ResolucionDeDiferencia; onCerrar: () => void; onHecho: (d: DiferenciaDeCoste) => void }) {
    const avisar = useAvisoBreve();
    const r = RESOLUCIONES[resolucion];
    const ajustar = resolucion === 'balance_adjusted';
    const [mensajes, setMensajes] = useState('');
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errores, setErrores] = useState<{ mensajes?: string; motivo?: string }>({});
    const errorMotivo = errores.motivo ?? (intentado && !motivoValido(motivo) ? ERROR_MOTIVO : null);
    const errorMensajes = errores.mensajes ?? (intentado && ajustar && mensajesDeAjuste(mensajes) === null ? ERROR_MENSAJES : null);
    const resolver = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        const n = mensajesDeAjuste(mensajes);
        if (!motivoValido(motivo) || (ajustar && n === null)) {
            return;
        }
        setOcupado(true);
        ops.resolveWhatsAppCostDiscrepancy(discrepancia.id, { resolution: resolucion, ...(ajustar && n !== null ? { adjustment_messages: n } : {}), reason: motivo.trim() })
            .then(onHecho)
            .catch((e: unknown) => {
                setOcupado(false);
                const de = { mensajes: errorDeCampo(e, 'adjustment_messages'), motivo: errorDeCampo(e, 'reason') };
                if (de.mensajes !== null || de.motivo !== null) {
                    setErrores({ mensajes: de.mensajes ?? undefined, motivo: de.motivo ?? undefined });
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };
    return (
        <Ventana abierta titulo={r.titulo} texto={textoResolver(fila, resolucion)} accion={r.boton} accionIcono={r.icono} ocupado={ocupado} movil={movil} onAccion={resolver} onCerrar={onCerrar}>
            {ajustar && (
                <Campo
                    etiqueta="Mensajes que se ajustan en su paquete"
                    tipo="number"
                    modo="numeric"
                    valor={mensajes}
                    onChange={(v) => {
                        setMensajes(v);
                        setErrores((x) => ({ ...x, mensajes: undefined }));
                    }}
                    error={errorMensajes}
                />
            )}
            <Campo
                etiqueta="Motivo"
                valor={motivo}
                onChange={(v) => {
                    setMotivo(v);
                    setErrores((x) => ({ ...x, motivo: undefined }));
                }}
                max={500}
                error={errorMotivo}
                ayuda="Obligatorio. Queda en Auditoría."
                onEnter={resolver}
            />
        </Ventana>
    );
}

/**
 * **Coste de WhatsApp** (RV Panel `formulario`, prototipo `pIsCoste`, `createWhatsAppCostRate`): categoría de Meta, país,
 * coste por mensaje sin IVA, desde qué trimestre vale y motivo. No se edita: el nuevo cierra el anterior.
 */
export function PanelCoste({ abierto, ops, movil, ahora, onCerrar, onHecho }: { abierto: boolean; ops: Operaciones; movil: boolean; ahora: Date; onCerrar: () => void; onHecho: () => void }) {
    return abierto ? <FormCoste ops={ops} movil={movil} ahora={ahora} onCerrar={onCerrar} onHecho={onHecho} /> : null;
}

function FormCoste({ ops, movil, ahora, onCerrar, onHecho }: { ops: Operaciones; movil: boolean; ahora: Date; onCerrar: () => void; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [fechas] = useState(() => iniciosDeTrimestre(ahora));
    const [categoria, setCategoria] = useState<CategoriaDeMeta>('marketing');
    const [pais, setPais] = useState('ES');
    const [coste, setCoste] = useState('');
    const [desde, setDesde] = useState(fechas[0]?.valor ?? '');
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errores, setErrores] = useState<{ coste?: string; motivo?: string }>({});
    // En diezmilésimas: el coste por mensaje lleva 4 decimales (G1149).
    const diezmilesimas = aDiezmilesimas(coste);
    const errorCoste = errores.coste ?? (intentado && diezmilesimas === null ? ERROR_COSTE : null);
    const errorMotivo = errores.motivo ?? (intentado && !motivoValido(motivo) ? ERROR_MOTIVO : null);
    const registrar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (diezmilesimas === null || !motivoValido(motivo)) {
            return;
        }
        setOcupado(true);
        ops.createWhatsAppCostRate({ pricing_category: categoria, country: pais, unit_cost: { amount: diezmilesimas, currency: 'EUR' }, valid_from: desde, reason: motivo.trim() })
            .then(() => {
                onHecho();
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const de = { coste: errorDeCampo(e, 'unit_cost.amount') ?? errorDeCampo(e, 'unit_cost'), motivo: errorDeCampo(e, 'reason') };
                if (de.coste !== null || de.motivo !== null) {
                    setErrores({ coste: de.coste ?? undefined, motivo: de.motivo ?? undefined });
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };
    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            titulo="Coste de WhatsApp"
            eyebrow="Conciliación YCloud"
            subtitulo="Las tarifas nuevas de Meta, cada trimestre."
            icono="scale"
            accion="Registrar el coste"
            accionIcono="check"
            ocupado={ocupado}
            onAccion={registrar}
        >
            <div className="rv-boy-form">
                <Desplegable
                    etiqueta="Categoría de Meta"
                    valor={categoria}
                    opciones={CATEGORIAS_COSTE}
                    onChange={(v) => {
                        setCategoria(CATEGORIAS_COSTE.find((c) => c.valor === v)?.valor ?? 'marketing');
                    }}
                />
                <Desplegable etiqueta="País" valor={pais} opciones={PAISES} onChange={setPais} />
                <Campo
                    etiqueta="Coste por mensaje, sin IVA"
                    tipo="number"
                    valor={coste}
                    onChange={(v) => {
                        setCoste(v);
                        setErrores((x) => ({ ...x, coste: undefined }));
                    }}
                    prefijo="€ "
                    error={errorCoste}
                    ayuda="Con hasta 4 decimales, por ejemplo 0,0585."
                />
                <Desplegable etiqueta="Vale desde" valor={desde} opciones={fechas} onChange={setDesde} />
                <Campo
                    etiqueta="Motivo"
                    valor={motivo}
                    onChange={(v) => {
                        setMotivo(v);
                        setErrores((x) => ({ ...x, motivo: undefined }));
                    }}
                    max={500}
                    error={errorMotivo}
                    ayuda="Obligatorio. Queda en Auditoría."
                />
            </div>
            <div className="rv-boy-nota">
                <Icono nombre="info" />
                {NOTA_COSTE}
            </div>
        </PanelLateral>
    );
}

/** Los meses que se pueden elegir para el extracto: el elegido en la pantalla y los 11 anteriores (nunca uno futuro). */
function mesesDelExtracto(mes: Mes, ahora: Date): Mes[] {
    const lista: Mes[] = [];
    for (let i = 0; i < 12; i++) {
        const total = mes.y * 12 + mes.m - i;
        const m = { y: Math.floor(total / 12), m: total % 12 };
        if (!mesFuturo(m, ahora)) {
            lista.push(m);
        }
    }
    return lista;
}

/**
 * **Importar el extracto de YCloud** (G1156, `importWhatsAppCostStatement`): se elige el CSV que se descarga de YCloud y el
 * número y el mes del extracto; el panel convierte el CSV en líneas, enseña la vista previa y los errores del archivo, y al
 * importar **se concilia** (sin un paso «Conciliar» aparte). El total sin IVA ya no se teclea: lo suma el back de las líneas
 * (G1172, ADR-0268).
 */
// Provisional hasta C.68: RV Panel formulario con los campos de siempre, hasta que diseño dibuje la subida.
export function PanelImportar(p: { abierto: boolean; ops: Operaciones; movil: boolean; mes: Mes; ahora: Date; onCerrar: () => void; onHecho: (e: ExtractoDeYCloud, mes: Mes) => void }) {
    return p.abierto ? <FormImportar {...p} /> : null;
}

function FormImportar({ ops, movil, mes, ahora, onCerrar, onHecho }: { ops: Operaciones; movil: boolean; mes: Mes; ahora: Date; onCerrar: () => void; onHecho: (e: ExtractoDeYCloud, mes: Mes) => void }) {
    const avisar = useAvisoBreve();
    const [meses] = useState(() => mesesDelExtracto(mes, ahora));
    const [archivo, setArchivo] = useState<string | null>(null);
    const [leido, setLeido] = useState<CsvLeido | null>(null);
    const [numero, setNumero] = useState('');
    const [periodo, setPeriodo] = useState(claveMes(mes));
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [deApi, setDeApi] = useState<Partial<Record<'numero' | 'lineas', string>>>({});
    const errores = {
        archivo: intentado && leido === null ? 'Elige el CSV que descargas de YCloud.' : null,
        numero: deApi.numero ?? (intentado && numero.trim() === '' ? 'Escribe el número del extracto.' : null),
    };

    const elegir = (f: File | undefined) => {
        setDeApi({});
        if (f === undefined) {
            return;
        }
        setArchivo(f.name);
        f.text()
            .then((texto) => {
                setLeido(leerCsvYCloud(texto));
            })
            .catch(() => {
                setLeido({ lineas: [], errores: ['No se ha podido leer el archivo.'] });
            });
    };

    const importar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (leido === null || leido.lineas.length === 0 || leido.errores.length > 0 || numero.trim() === '') {
            return;
        }
        setOcupado(true);
        const delMes = meses.find((m) => claveMes(m) === periodo) ?? mes;
        // G1172, G1197: el total no se teclea ni se manda (`total_excl_tax` es opcional); lo suma el back de las líneas.
        ops.importWhatsAppCostStatement({ issuer: 'ycloud', number: numero.trim(), period: periodo, lines: leido.lineas })
            .then((e) => {
                onHecho(e, delMes);
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const api = { numero: errorDeCampo(e, 'number'), lineas: errorDeCampo(e, 'lines') };
                if (Object.values(api).some((x) => x !== null)) {
                    setDeApi(Object.fromEntries(Object.entries(api).filter(([, x]) => x !== null)));
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };

    const vista = leido?.lineas.slice(0, 8) ?? [];
    const fallos = [...(leido?.errores ?? []), ...(deApi.lineas === undefined ? [] : [deApi.lineas])];
    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            titulo="Importar el extracto de YCloud"
            eyebrow="Conciliación YCloud"
            subtitulo="Sube el CSV que descargas de YCloud. Se concilia al importar."
            icono="upload"
            accion="Importar y conciliar"
            accionIcono="check"
            ocupado={ocupado}
            ocupadoTexto="Importando…"
            onAccion={importar}
        >
            <div className="rv-boy-form">
                <label className={`rv-boy-archivo${errores.archivo === null ? '' : ' rv-boy-archivo--error'}`}>
                    <Icono nombre="file-spreadsheet" />
                    <span>{archivo ?? 'Elegir el archivo CSV'}</span>
                    <input
                        type="file"
                        accept=".csv,text/csv"
                        aria-label="Archivo CSV de YCloud"
                        onChange={(e) => {
                            elegir(e.target.files?.[0]);
                        }}
                    />
                </label>
                {errores.archivo !== null && (
                    <span role="alert" className="rv-boy-error">
                        <Icono nombre="circle-alert" />
                        {errores.archivo}
                    </span>
                )}
                <Campo
                    etiqueta="Número del extracto"
                    valor={numero}
                    onChange={(v) => {
                        setNumero(v);
                        setDeApi({});
                    }}
                    max={60}
                    error={errores.numero}
                />
                <Desplegable etiqueta="Mes del extracto" valor={periodo} opciones={meses.map((m) => ({ valor: claveMes(m), texto: textoDelMes(m) }))} onChange={setPeriodo} />
            </div>
            {fallos.length > 0 && (
                <div className="rv-boy-errores" role="alert" aria-label="Errores del archivo">
                    {fallos.map((f) => (
                        <span key={f}>
                            <Icono nombre="circle-alert" />
                            {f}
                        </span>
                    ))}
                </div>
            )}
            {leido !== null && leido.lineas.length > 0 && (
                <div className="rv-boy-vista" role="table" aria-label="Vista previa de las líneas">
                    <div className="rv-boy-vista__cab">{leido.lineas.length === 1 ? '1 línea' : `${String(leido.lineas.length)} líneas`}</div>
                    {vista.map((l, i) => (
                        <div key={`${l.waba_ref}-${l.pricing_category}-${l.country}-${String(i)}`} className="rv-boy-vista__fila" role="row">
                            <span role="cell">
                                <strong>{l.waba_ref}</strong>
                                <span>{`${CATEGORIAS[l.pricing_category]} · ${l.country} · ${String(l.billable_messages)} mensajes`}</span>
                            </span>
                            <span role="cell" className="rv-boy-num">
                                {dinero(l.amount)}
                            </span>
                        </div>
                    ))}
                    {leido.lineas.length > vista.length && <div className="rv-boy-vista__mas">{`y ${String(leido.lineas.length - vista.length)} más`}</div>}
                </div>
            )}
        </PanelLateral>
    );
}

/** **Registrar una recarga** del monedero de YCloud (`createYCloudWalletTopUp`, G1157): importe en dólares y referencia. */
// Provisional hasta C.69: RV Ventana con los campos de siempre, hasta que diseño la dibuje.
export function VentanaRecarga({ ops, movil, onCerrar, onHecho }: { ops: Operaciones; movil: boolean; onCerrar: () => void; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [importe, setImporte] = useState('');
    const [referencia, setReferencia] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [deApi, setDeApi] = useState<Partial<Record<'importe' | 'referencia', string>>>({});
    const centimos = aCentimos(importe);
    const errores = {
        importe: deApi.importe ?? (intentado ? errorDeImporte(importe, true) : null),
        referencia: deApi.referencia ?? (intentado && referencia.trim() === '' ? ERROR_REFERENCIA_RECARGA : null),
    };
    const guardar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (centimos === null || centimos === 0 || referencia.trim() === '') {
            return;
        }
        setOcupado(true);
        ops.createYCloudWalletTopUp({ amount: { amount: centimos, currency: MONEDA_MONEDERO }, reference: referencia.trim() })
            .then(() => {
                onHecho();
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const api = { importe: errorDeCampo(e, 'amount') ?? errorDeCampo(e, 'amount.amount'), referencia: errorDeCampo(e, 'reference') };
                if (Object.values(api).some((x) => x !== null)) {
                    setDeApi(Object.fromEntries(Object.entries(api).filter(([, x]) => x !== null)));
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };
    return (
        <Ventana
            abierta
            titulo="Registrar una recarga"
            texto="Lo que has recargado en el monedero de prepago de YCloud, en dólares, con la referencia del pago. Queda en Auditoría."
            accion="Registrar la recarga"
            accionIcono="wallet"
            ocupado={ocupado}
            movil={movil}
            onAccion={guardar}
            onCerrar={onCerrar}
        >
            <CampoImporte
                etiqueta="Importe"
                moneda={MONEDA_MONEDERO}
                valor={importe}
                onChange={(v) => {
                    setImporte(v);
                    setDeApi({});
                }}
                error={errores.importe}
            />
            <Campo
                etiqueta="Referencia del pago"
                valor={referencia}
                onChange={(v) => {
                    setReferencia(v);
                    setDeApi({});
                }}
                max={120}
                error={errores.referencia}
                ayuda="La de la transferencia o el recibo."
                onEnter={guardar}
            />
        </Ventana>
    );
}
